import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import ApiService from "../services/ApiService";
import Loader from "./Loader";
import { todayInUAEDate } from "../utils/timezone";
import "./ManualBookingOffcanvas.css";

const toYMD = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "HH:MM" -> minutes since midnight.
const toMinutes = (t) => {
  if (!t) return NaN;
  const [h, m] = t.split(":").map(Number);
  return Number.isNaN(h) || Number.isNaN(m) ? NaN : h * 60 + m;
};

/** The slot's own length (end - start) — the session all its services must add up to. */
const slotMinutes = (slot) => {
  if (!slot?.start_time || !slot?.end_time) return 0;
  const diff = toMinutes(slot.end_time) - toMinutes(slot.start_time);
  return Number.isNaN(diff) || diff <= 0 ? 0 : diff;
};

// Icebox slots run a fixed split: the ice service takes 10 min, every other
// service takes 20 min (10 + 20 = the app's 30m icebox session) — mirrors
// mobile/components/service-sheet.tsx exactly, so "Ice Lab" + one other
// service is the only way to fill an icebox slot.
const ICE_MINUTES = 10;
const OTHER_MINUTES = 20;

const emptyForm = {
  guest_name: "",
  guest_phone: "",
  grand_total: "",
  payment_status: "Paid",
  payment_method: "cash",
  payment_by: "session",
};

/**
 * Creates a walk-in/guest booking. Two modes:
 *  - "pick": no slot chosen yet (opened via the page's "New Manual Booking"
 *    button) — admin picks a room + date, then a free slot from that room's
 *    live day-slots grid.
 *  - "form": a slot is already known (either passed in via `prefill`, from
 *    clicking an available pill on the calendar, or chosen in pick mode) —
 *    shows the guest/price form. "Change slot" drops back to pick mode.
 */
const ManualBookingOffcanvas = ({ show, onClose, rooms, prefill, onCreated }) => {
  const [mode, setMode] = useState("pick");
  const [slot, setSlot] = useState(null); // { room_id, room_title, is_icebox_room, date, start_time, end_time, is_iced_slot }

  const [pickRoomId, setPickRoomId] = useState("");
  const [pickDate, setPickDate] = useState(toYMD(todayInUAEDate()));
  const [pickSlots, setPickSlots] = useState([]);
  const [pickLoading, setPickLoading] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [picks, setPicks] = useState({}); // { [service_id]: minutes }
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the panel opens.
  useEffect(() => {
    if (!show) return;
    setForm(emptyForm);
    if (prefill) {
      setSlot(prefill);
      setMode("form");
    } else {
      setSlot(null);
      setMode("pick");
      setPickRoomId(rooms?.[0]?._id || "");
      setPickDate(toYMD(todayInUAEDate()));
      setPickSlots([]);
    }
  }, [show, prefill, rooms]);

  // Services list, loaded once when the panel first opens.
  useEffect(() => {
    if (!show || services.length) return;
    (async () => {
      try {
        const res = await ApiService.request({ method: "GET", url: "admin/services-list" });
        if (res.data.status) setServices(res.data.data || []);
      } catch {
        // handled globally
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadPickSlots = useCallback(async (room_id, date) => {
    if (!room_id || !date) return;
    try {
      setPickLoading(true);
      const res = await ApiService.request({
        method: "GET",
        url: `admin/bookings/day-slots?date=${date}&room_id=${room_id}`,
      });
      if (res.data.status) setPickSlots(res.data.data?.rooms?.[0]?.slots || []);
      else { setPickSlots([]); toast.error(res.data.message); }
    } catch {
      toast.error("Failed to load slots");
    } finally {
      setPickLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === "pick" && show) loadPickSlots(pickRoomId, pickDate);
  }, [mode, show, pickRoomId, pickDate, loadPickSlots]);

  const chooseSlot = (s) => {
    const room = rooms.find((r) => r._id === pickRoomId);
    setSlot({
      room_id: pickRoomId,
      room_title: room?.title || "Room",
      is_icebox_room: room?.is_icebox_room || false,
      date: pickDate,
      start_time: s.start_time,
      end_time: s.end_time,
      is_iced_slot: s.is_iced_slot,
    });
    setMode("form");
  };

  const backToPick = () => {
    if (slot) { setPickRoomId(slot.room_id); setPickDate(slot.date); }
    setMode("pick");
  };

  /* ── Session composition — mirrors the app's Choose Service sheet ──
   * An icebox slot offers every service (icebox + standard) with the fixed
   * 10/20 split; a standard slot offers only standard services, each keeping
   * its own selectable durations. Either way the picks must sum to exactly
   * the slot's own length before the booking can be created.
   */
  const sessionMinutes = useMemo(() => slotMinutes(slot), [slot]);
  const showIcebox = slot?.is_iced_slot ?? false;

  const displayServices = useMemo(() => {
    if (!services.length) return [];
    if (showIcebox) {
      const ice = services.find((s) => /ice/i.test(s.title));
      return services.map((s) => ({
        ...s,
        durations: ice && s._id === ice._id ? [ICE_MINUTES] : [OTHER_MINUTES],
      }));
    }
    return services
      .filter((s) => !s.is_icebox_service)
      .map((s) => ({
        ...s,
        durations: (s.duration_options || [])
          .map((o) => Number(o.duration))
          .filter((n) => Number.isFinite(n) && n > 0),
      }))
      .filter((s) => s.durations.length > 0);
  }, [services, showIcebox]);

  // Reset picks whenever a new slot is chosen; an icebox slot starts with its
  // mandatory ice service pre-selected, same as the app.
  useEffect(() => {
    if (!slot) return;
    if (showIcebox && displayServices.length) {
      const ice = displayServices.find((s) => /ice/i.test(s.title));
      setPicks(ice ? { [ice._id]: ice.durations[0] } : {});
    } else {
      setPicks({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slot, showIcebox, displayServices.length]);

  const totalMinutes = Object.values(picks).reduce((sum, m) => sum + (m || 0), 0);
  const sessionComplete = sessionMinutes > 0 && totalMinutes === sessionMinutes;

  // Tapping the chip already picked for a service clears it; tapping another
  // chip replaces that service's pick (a service holds one duration at a time).
  const toggleService = (serviceId, minutes) => {
    setPicks((current) => {
      if (current[serviceId] === minutes) {
        const { [serviceId]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [serviceId]: minutes };
    });
  };

  const suggestedTotal = Object.entries(picks).reduce((sum, [serviceId, minutes]) => {
    const svc = services.find((s) => s._id === serviceId);
    const opt = svc?.duration_options?.find((o) => Number(o.duration) === minutes);
    return sum + (Number(opt?.price) || 0);
  }, 0);

  const applySuggestedTotal = () => setForm((p) => ({ ...p, grand_total: String(suggestedTotal) }));

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.guest_name.trim()) { toast.error(" Customer name is required"); return; }
    if (form.grand_total === "" || Number.isNaN(Number(form.grand_total)) || Number(form.grand_total) < 0) {
      toast.error("Enter a valid price");
      return;
    }
    if (!sessionComplete) {
      toast.error(
        totalMinutes > 0
          ? `Add ${sessionMinutes - totalMinutes} more minutes to complete the session.`
          : `Select services totalling ${sessionMinutes} minutes.`
      );
      return;
    }
    const serviceIds = Object.entries(picks).map(([service_id, duration]) => ({
      service_id,
      duration: String(duration),
    }));

    setSubmitting(true);
    try {
      const res = await ApiService.request({
        method: "POST",
        url: "admin/bookings/manual",
        data: {
          room_id: slot.room_id,
          date: slot.date,
          time: slot.start_time,
          guest_name: form.guest_name.trim(),
          guest_phone: form.guest_phone.trim() || undefined,
          services: serviceIds,
          grand_total: Number(form.grand_total),
          payment_status: form.payment_status,
          payment_method: form.payment_method,
          payment_by: form.payment_by,
          // Walk-in bookings are always Confirmed — there is no Pending option
          // for a guest booking (see admin bookingController.createManual).
          status: "Confirmed",
        },
      });
      if (res.data.status) {
        toast.success(res.data.message || "Booking created successfully!");
        onCreated?.();
        onClose();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      // handled globally
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className={`offcanvas offcanvas-end manual-booking-offcanvas ${show ? "show" : ""}`}
        tabIndex="-1"
        style={{ visibility: show ? "visible" : "hidden", width: 460 }}
      >
        <div className="offcanvas-header">
          {/* Phones: a prominent Back in place of the small close cross. */}
          <button type="button" className="offcanvas-back" onClick={onClose} data-app-back>
            <i className="bi bi-arrow-left"></i>Back
          </button>
          <h5 className="offcanvas-title">
            {mode === "pick" ? "New Manual Booking" : "Walking Booking Details"}
          </h5>
          <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
        </div>

        <div className="offcanvas-body manual-booking-body">
          {mode === "pick" && (
            <div className="mb-pick">
              <div className="mb-6">
                <label className="form-label">Room</label>
                <select
                  className="form-select form-control-solid"
                  value={pickRoomId}
                  onChange={(e) => setPickRoomId(e.target.value)}
                >
                  {(rooms || []).map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.title} {r.is_icebox_room ? "(Icebox)" : "(Standard)"}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control form-control-solid"
                  value={pickDate}
                  onChange={(e) => setPickDate(e.target.value)}
                />
              </div>

              <label className="form-label">Available Slots</label>
              {pickLoading ? (
                <div className="text-center py-4"><Loader text="Loading" size="24" /></div>
              ) : pickSlots.length === 0 ? (
                <div className="mb-empty-slots">No slots for this room on this day.</div>
              ) : (
                <div className="mb-slot-grid">
                  {pickSlots.map((s) => (
                    <button
                      key={s.start_time}
                      type="button"
                      disabled={s.is_booked}
                      className={`mb-slot-pill ${s.is_booked ? "booked" : "available"}`}
                      onClick={() => chooseSlot(s)}
                    >
                      {s.is_iced_slot && <i className="bi bi-snow2"></i>}
                      <span className="mb-slot-time">{s.start_time} – {s.end_time}</span>
                      {s.is_booked && <span className="mb-slot-booked-tag">Booked</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {mode === "form" && slot && (
            <form onSubmit={handleSubmit}>
              <div className="mb-slot-summary">
                <div className="mb-slot-summary-row">
                  <span className={`badge ${slot.is_icebox_room ? "badge-light-info" : "badge-light-secondary text-dark"}`}>
                    {slot.is_icebox_room ? "Icebox" : "Standard"}
                  </span>
                  <strong>{slot.room_title}</strong>
                </div>
                <div className="mb-slot-summary-row text-muted">
                  <i className="bi bi-calendar3 me-2"></i>
                  {slot.date}
                  <i className="bi bi-clock ms-3 me-2"></i>
                  {slot.start_time} - {slot.end_time}
                  {slot.is_iced_slot && <i className="bi bi-snow2 ms-2" title="Icebox slot"></i>}
                </div>
                <button type="button" className="mb-change-slot" onClick={backToPick}>
                  Change slot
                </button>
              </div>

              <div className="mb-6">
                <label className="required form-label">Walking Customer Name</label>
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="e.g. John Smith"
                  value={form.guest_name}
                  onChange={(e) => setForm((p) => ({ ...p, guest_name: e.target.value }))}
                />
              </div>

              <div className="mb-6">
                <label className="form-label">Walking Customer Phone</label>
                <input
                  type="text"
                  className="form-control form-control-solid"
                  placeholder="e.g. +971 50 123 4567"
                  value={form.guest_phone}
                  onChange={(e) => setForm((p) => ({ ...p, guest_phone: e.target.value }))}
                />
              </div>

              <div className="mb-6">
                <h6 className="mb-session-title">
                  Customize the <span className="mb-session-accent">{sessionMinutes} Minute</span> Session
                </h6>
                <p className="mb-session-subtitle">
                  {showIcebox
                    ? "This icebox slot needs the ice service plus one more service to fill the session."
                    : "Select the services that make up this session."}
                </p>

                <div className="mb-tally">
                  <div className="mb-tally-track">
                    <div
                      className="mb-tally-fill"
                      style={{ width: `${sessionMinutes ? Math.min(100, (totalMinutes / sessionMinutes) * 100) : 0}%` }}
                    />
                  </div>
                  <span className={`mb-tally-label ${sessionComplete ? "complete" : ""}`}>
                    {totalMinutes}/{sessionMinutes} min
                  </span>
                </div>

                {displayServices.length === 0 ? (
                  <div className="mb-empty-slots">No services available for this session.</div>
                ) : (
                  displayServices.map((svc) => {
                    const picked = picks[svc._id];
                    const single = svc.durations.length === 1;
                    // Mirrors the app: ice(10) + one 20-min service is the only
                    // combination that fills a 30m icebox slot, so deselecting ice
                    // always leaves the session unfinishable — lock it in instead.
                    const isMandatoryIce = showIcebox && /ice/i.test(svc.title);
                    return (
                      <div className={`mb-service-card ${picked != null ? "active" : ""}`} key={svc._id}>
                        <div className={`mb-service-icon ${svc.is_icebox_service ? "ice" : ""}`}>
                          {svc.image ? (
                            <img src={svc.image} alt={svc.title} />
                          ) : (
                            <i className={`bi ${svc.is_icebox_service ? "bi-snow2" : "bi-droplet"}`}></i>
                          )}
                        </div>
                        <div className="mb-service-body">
                          <div className="mb-service-name">{svc.title}</div>
                          {single && (
                            <span className="mb-service-note">
                               {`${svc.durations[0]} min only`}
                            </span>
                          )}
                        </div>
                        <div className="mb-chip-row">
                          {svc.durations.map((minutes) => {
                            const active = picked === minutes;
                            const withoutThis = totalMinutes - (picked ?? 0);
                            const blocked = !active && withoutThis + minutes > sessionMinutes;
                            return (
                              <button
                                type="button"
                                key={minutes}
                                className={`mb-chip ${active ? "active" : ""} ${blocked ? "blocked" : ""}`}
                                disabled={blocked || isMandatoryIce}
                                onClick={() => toggleService(svc._id, minutes)}
                              >
                                {minutes} min
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}

                {!sessionComplete && sessionMinutes > 0 && (
                  <div className="mb-session-hint">
                    {totalMinutes > 0
                      ? `Add ${sessionMinutes - totalMinutes} more minutes to complete the session.`
                      : `Select services totalling ${sessionMinutes} minutes.`}
                  </div>
                )}
              </div>

              <div className="mb-6">
                <label className="required form-label">Price (AED)</label>
                <div className="d-flex gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control form-control-solid"
                    placeholder="Custom session price"
                    value={form.grand_total}
                    onChange={(e) => setForm((p) => ({ ...p, grand_total: e.target.value }))}
                  />
                  {suggestedTotal > 0 && (
                    <button type="button" className="btn btn-light mb-suggest-btn" onClick={applySuggestedTotal}>
                      Use AED {suggestedTotal}
                    </button>
                  )}
                </div>
                <div className="form-text">This is charged as entered — it does not have to match any plan or service price.</div>
              </div>

              <div className="row">
                {/* <div className="col-6 mb-6">
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-select form-control-solid"
                    value={form.payment_status}
                    onChange={(e) => setForm((p) => ({ ...p, payment_status: e.target.value }))}
                  >
                    <option value="Paid">Paid</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div> */}
                <div className="col-12 mb-6">
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select form-control-solid"
                    value={form.payment_method}
                    onChange={(e) => setForm((p) => ({ ...p, payment_method: e.target.value }))}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    {/* <option value="tabby">Tabby</option>
                    <option value="apple_pay">Apple Pay</option> */}
                  </select>
                </div>
              </div>
{/* 
              <div className="mb-6">
                <label className="form-label">Pay By</label>
                <select
                  className="form-select form-control-solid"
                  value={form.payment_by}
                  onChange={(e) => setForm((p) => ({ ...p, payment_by: e.target.value }))}
                >
                  <option value="session">Session</option>
                  <option value="membership">Membership</option>
                </select>
              </div> */}

              <div className="d-flex justify-content-end gap-3 mt-4 mb-form-actions">
                <button type="button" className="btn btn-light" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-base" disabled={submitting || !sessionComplete}>
                  {submitting ? <Loader text="Saving" size="18" /> : "Create Booking"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {show && <div className="offcanvas-backdrop fade show" onClick={onClose}></div>}
    </>
  );
};

export default ManualBookingOffcanvas;
